import React, { useState, useEffect, useContext, useMemo } from 'react';
import styles from './MessageTemplates.module.css';
import { MdOutlineRadioButtonChecked, MdOutlineRadioButtonUnchecked } from 'react-icons/md';
import { AiOutlineClose } from 'react-icons/ai';
import TemplatePreview from './TemplatePreview';
import { BASE_URL_API } from '../../../References/URLs';
import AuthContext from '../../../store/auth-context';
import ActiveStoreContext from '../../../store/ActiveStoreContext';
import { FaFilter, FaSearch, FaSync } from 'react-icons/fa';
import { Image as ImageIconLucide, Film, FileText, Type } from 'lucide-react';
import StepHeader from './shared/StepHeader';

const MessageTemplates = ({ data, getCampaignDetails, setData }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({
        category: '',
        language: '',
        search: '',
    });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    // Mobile only — filter + sort controls live inline on desktop, but on a
    // small screen they collapse into a single "Filters & Sort" button that
    // opens this as a bottom sheet instead.
    const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

    useEffect(() => {
        getAllTemplates()
    }, []);

    useEffect(() => {
        // If selectedTemplate is set, update the message preview
        if (selectedTemplate) {
            const updatedMessage = handleVariableChange(selectedTemplate, selectedTemplate.variables.reduce((acc, v) => {
                acc[v.name] = v.value || v.defaultValue;
                return acc;
            }, {}));
            setData({ ...data, messageTemplate: selectedTemplate, message: updatedMessage });
        }
    }, [selectedTemplate]);

    const formatLabel = (value) => {
        if (!value) return '';
        return value
            .toString()
            .replace(/[_-]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const deliverySourceLabel = (value) => {
        if (!value) return '';
        if (value.toUpperCase() === 'META') {
            return 'WhatsApp';
        }
        return formatLabel(value);
    };

    const normalize = (value) => (value ?? '').toString().toLowerCase().trim();

    const getValueByPath = (target, path) => {
        if (!path) return undefined;
        return path.split('.').reduce((acc, segment) => (
            acc && acc[segment] !== undefined ? acc[segment] : undefined
        ), target);
    };

    const filterOptions = useMemo(() => {
        const categories = new Map();
        const languages = new Map();

        templates.forEach((template) => {
            if (template?.category) {
                categories.set(template.category, formatLabel(template.category));
            }
            if (template?.language) {
                languages.set(template.language, formatLabel(template.language));
            }
        });

        const toSortedArray = (map) => Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return {
            categories: toSortedArray(categories),
            languages: toSortedArray(languages),
        };
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        if (!Array.isArray(templates) || templates.length === 0) {
            return [];
        }

        const searchTerm = normalize(filters.search);
        const categoryFilter = normalize(filters.category);
        const languageFilter = normalize(filters.language);

        let data = [...templates];

        data = data.filter((template) => {
            const matchesCategory = !categoryFilter || normalize(template?.category).includes(categoryFilter);
            const matchesLanguage = !languageFilter || normalize(template?.language) === languageFilter;

            let matchesSearch = true;
            if (searchTerm) {
                matchesSearch = [
                    template?.templateNamePlatform,
                    template?.templateName,
                    template?.name,
                    template?.templateIdentifier,
                    template?.category,
                    template?.status,
                    template?.language,
                ].some((field) => normalize(field).includes(searchTerm));
            }

            return matchesCategory && matchesLanguage && matchesSearch;
        });

        if (sortConfig.key) {
            const direction = sortConfig.direction === 'desc' ? -1 : 1;
            data.sort((a, b) => {
                const valueA = getValueByPath(a, sortConfig.key) ?? '';
                const valueB = getValueByPath(b, sortConfig.key) ?? '';

                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return (valueA - valueB) * direction;
                }

                return normalize(valueA).localeCompare(normalize(valueB)) * direction;
            });
        } else {
            data.sort((a, b) => {
                const dateA = Date.parse(a?.lastEdited?.date || a?.updatedOn || a?.createdOn || 0);
                const dateB = Date.parse(b?.lastEdited?.date || b?.updatedOn || b?.createdOn || 0);
                return dateB - dateA;
            });
        }

        return data;
    }, [templates, filters, sortConfig]);

    const totalTemplatesCount = templates.length;
    const filteredTemplatesCount = filteredTemplates.length;

    const totalPages = useMemo(() => (
        Math.max(1, Math.ceil((filteredTemplatesCount || 0) / rowsPerPage))
    ), [filteredTemplatesCount, rowsPerPage]);

    const paginatedTemplates = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredTemplates.slice(start, start + rowsPerPage);
    }, [filteredTemplates, currentPage, rowsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const hasActiveFilters = useMemo(() => (
        Boolean(
            filters.category
            || filters.language
            || (filters.search && filters.search.trim() !== '')
        )
    ), [filters]);

    const displayTotalPages = filteredTemplatesCount === 0 ? 0 : totalPages;
    const displayCurrentPage = filteredTemplatesCount === 0 ? 0 : currentPage;

    const handleSort = (key) => {
        setSortConfig((prev) => {
            const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
            return { key, direction };
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };


    const handleVariableChange = (template, variableValues) => {
        if (!template || !template.content) return '';
        return template.content.replace(/{(\w+)}/g, (_, key) => variableValues[key] || `{${key}}`);
    };


    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handlePageChange = (direction) => {
        if (direction === 'prev' && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        } else if (direction === 'next' && currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const resetFilters = () => {
        setFilters({
            category: '',
            language: '',
            search: '',
        });
        setSortConfig({ key: null, direction: null });
        setCurrentPage(1);
    };

    const SORT_FIELDS = [
        { key: 'templateNamePlatform', label: 'Name' },
        { key: 'deliverySource', label: 'Delivery' },
        { key: 'status', label: 'Status' },
        { key: 'lastEdited.date', label: 'Last edited' },
    ];

    // Lightweight WhatsApp-markdown + {{n}} variable highlighter for the
    // card snippet — a scaled-down version of what TemplatePreviewWhatsApp
    // does for the full phone mockup, just enough to make each card read
    // like an actual message instead of a wall of plain text.
    const renderSnippet = (content) => {
        if (!content) {
            return <span className={styles.snippetEmpty}>No preview text available</span>;
        }
        const normalized = content.replace(/\r\n/g, '\n').trim();
        const lines = normalized.split('\n').filter((l) => l.trim() !== '').slice(0, 6);
        return lines.map((line, li) => {
            const tokenRegex = /(\*[^*]+\*)|(_[^_]+_)|({{\d+}})/g;
            const parts = [];
            let lastIndex = 0;
            let match;
            while ((match = tokenRegex.exec(line)) !== null) {
                if (match.index > lastIndex) parts.push({ type: 'text', value: line.slice(lastIndex, match.index) });
                const token = match[0];
                if (token.startsWith('*')) parts.push({ type: 'bold', value: token.slice(1, -1) });
                else if (token.startsWith('_')) parts.push({ type: 'italic', value: token.slice(1, -1) });
                else parts.push({ type: 'var', value: token });
                lastIndex = match.index + token.length;
            }
            if (lastIndex < line.length) parts.push({ type: 'text', value: line.slice(lastIndex) });

            return (
                <span key={li} className={styles.snippetLine}>
                    {parts.map((part, pi) => {
                        if (part.type === 'bold') return <strong key={pi}>{part.value}</strong>;
                        if (part.type === 'italic') return <em key={pi}>{part.value}</em>;
                        if (part.type === 'var') return <span key={pi} className={styles.varToken}>{part.value}</span>;
                        return <React.Fragment key={pi}>{part.value}</React.Fragment>;
                    })}
                </span>
            );
        });
    };

    // Not every template has a header, and not every header has usable
    // content (headerContent is frequently null even when headerType is
    // set) — this renders an honest placeholder ("this template has an
    // image slot") rather than a broken image icon or nothing at all.
    const renderHeaderPreview = (template) => {
        const type = template?.headerType;
        if (!type) return null;
        const content = template?.headerContent;

        if (type === 'image') {
            // headerContent is the value a campaign actually sends (set once
            // the user picks/uploads an image for this template) and is
            // usually still empty at this stage — sample_header_image is
            // what the template was designed with, so it's shown instead
            // whenever there's nothing campaign-specific to prefer yet.
            const imageSrc = (content && /^https?:\/\//.test(content)) ? content : template?.sample_header_image;
            if (imageSrc) {
                return <img src={imageSrc} alt="" className={styles.headerImg} />;
            }
            return (
                <div className={`${styles.headerPlaceholder} ${styles.headerPlaceholderImage}`}>
                    <ImageIconLucide size={16} />
                    <span>Image header</span>
                </div>
            );
        }
        if (type === 'video') {
            return (
                <div className={`${styles.headerPlaceholder} ${styles.headerPlaceholderVideo}`}>
                    <Film size={16} />
                    <span>Video header</span>
                </div>
            );
        }
        if (type === 'document') {
            return (
                <div className={`${styles.headerPlaceholder} ${styles.headerPlaceholderDocument}`}>
                    <FileText size={16} />
                    <span>Document header</span>
                </div>
            );
        }
        if (type === 'text' && content) {
            return (
                <div className={`${styles.headerPlaceholder} ${styles.headerPlaceholderText}`}>
                    <Type size={16} />
                    <span>{content}</span>
                </div>
            );
        }
        return null;
    };

    const getAllTemplates = async () => {
        setLoading(true);
        try {
            const getAllTemplatesResponse = await fetch(BASE_URL_API + "getAllTemplates",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                    })
                });

            if (!getAllTemplatesResponse.ok) {
                throw new Error('Server error while fetching templates');
            }

            const getAllTemplatesRespo = await getAllTemplatesResponse.json();
            if (getAllTemplatesRespo.status === "success") {
                setTemplates(getAllTemplatesRespo?.response || []);
            } else if (getAllTemplatesRespo.message === "tokenExpired") {
                authCtx.logout();
            } else {
                setTemplates([]);
            }
        } catch (error) {
            console.error(error);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.mainContainer}>
                <StepHeader
                    eyebrow="Step 2 of 5"
                    title="Message Templates"
                    subtitle="Review, filter, and preview CRM-ready templates."
                    right={
                        <>
                            <span className="ccBadge">
                                {filteredTemplatesCount} / {totalTemplatesCount} templates
                            </span>
                            <button
                                type="button"
                                className={styles.filterToggleBtn}
                                onClick={() => setMobileSheetOpen((prev) => !prev)}
                            >
                                <FaFilter />
                                Filters &amp; Sort
                                {(hasActiveFilters || sortConfig.key) && <span className={styles.filterToggleDot} />}
                            </button>
                            <button
                                type="button"
                                className={`ccBtn ccBtnSecondary ${styles.refreshBtn}`}
                                onClick={getAllTemplates}
                                disabled={loading}
                            >
                                <FaSync className={loading ? styles.spin : ''} />
                                Refresh
                            </button>
                        </>
                    }
                />
                <div className={styles.toolbar}>
                    <div className={styles.toolbarRow}>
                        <div className={styles.searchWrapper}>
                            <div className={styles.searchBar}>
                                <FaSearch className={styles.icon} />
                                <input
                                    type="text"
                                    placeholder="Search templates by name, category, or identifier..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                                {filters.search && (
                                    <button
                                        type="button"
                                        className={styles.clearSearch}
                                        onClick={() => handleFilterChange('search', '')}
                                        aria-label="Clear search"
                                    >
                                        <AiOutlineClose size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className={styles.filterGroup}>
                            <select
                                className={styles.filterSelect}
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            >
                                <option value="">All categories</option>
                                {filterOptions.categories.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <select
                                className={styles.filterSelect}
                                value={filters.language}
                                onChange={(e) => handleFilterChange('language', e.target.value)}
                            >
                                <option value="">All languages</option>
                                {filterOptions.languages.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className={styles.resetBtn}
                                onClick={resetFilters}
                                disabled={!hasActiveFilters && sortConfig.key === null}
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
                <div className={styles.bodyContent}>
                    <div className={styles.sortRow}>
                        <span className={styles.sortLabel}>Sort by</span>
                        {SORT_FIELDS.map((field) => (
                            <button
                                key={field.key}
                                type="button"
                                className={`${styles.sortBtn} ${sortConfig.key === field.key ? styles.sortBtnActive : ''}`}
                                onClick={() => handleSort(field.key)}
                            >
                                {field.label}
                                {sortConfig.key === field.key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tableFooter}>
                        <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                            <option value={5}>5 Rows</option>
                            <option value={10}>10 Rows</option>
                            <option value={20}>20 Rows</option>
                            <option value={50}>50 Rows</option>
                        </select>
                        <div className={styles.pagination}>
                            <button onClick={() => handlePageChange('prev')} disabled={currentPage === 1 || filteredTemplatesCount === 0}>
                                Previous
                            </button>
                            <span>
                                Page {displayCurrentPage} of {displayTotalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange('next')}
                                disabled={currentPage >= totalPages || filteredTemplatesCount === 0}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                    <div className={styles.cardGridWrapper}>
                        {loading ? (
                            <div className={styles.loaderRow}>
                                <FaSync className={`${styles.loaderIcon} ${styles.spin}`} />
                                <span>Loading templates...</span>
                            </div>
                        ) : paginatedTemplates.length === 0 ? (
                            <div className={styles.emptyState}>No templates match the current filters.</div>
                        ) : (
                            <div className={styles.cardGrid}>
                                {paginatedTemplates.map((template, index) => {
                                    const deliveryLabel = deliverySourceLabel(template?.deliverySource) || '—';
                                    const deliveryTagClass = `${styles.deliveryTag} ${deliveryLabel === 'WhatsApp' ? styles.deliveryTagWhatsApp : ''}`;
                                    const isRowSelected = data?.templateIdentifier === template.templateIdentifier;
                                    const displayName = formatLabel(template.templateNamePlatform || template.templateName || 'Untitled Template');
                                    const headerPreview = renderHeaderPreview(template);

                                    const metaChips = [template.language, template.status, template.lastEdited?.date || template.updatedOn || template.createdOn]
                                        .filter((v) => v != null && v !== '');

                                    const openPreview = (e) => {
                                        e.stopPropagation();
                                        setPreviewTemplate(template);
                                        setPreviewVisible(true);
                                    };

                                    return (
                                        <div
                                            key={template?.templateIdentifier || index}
                                            className={`${styles.templateCard} ${isRowSelected ? styles.templateCardSelected : ''}`}
                                            onClick={openPreview}
                                        >
                                            <div className={styles.cardTopRow}>
                                                {isRowSelected ? (
                                                    <MdOutlineRadioButtonChecked className={styles.selectedIcon} />
                                                ) : (
                                                    <MdOutlineRadioButtonUnchecked className={styles.unselectedIcon} />
                                                )}
                                                <div className={deliveryTagClass}>{deliveryLabel}</div>
                                            </div>

                                            {headerPreview && (
                                                <div className={styles.cardHeaderPreview}>{headerPreview}</div>
                                            )}

                                            <div className={styles.cardName} title={template.templateNamePlatform}>{displayName}</div>
                                            <div className={styles.cardRawName}>{template.templateNamePlatform}</div>

                                            <div className={styles.cardSnippet}>
                                                {renderSnippet(template.content)}
                                                <div className={styles.snippetFade} />
                                            </div>

                                            {metaChips.length > 0 && (
                                                <div className={styles.cardMetaRow}>
                                                    {metaChips.map((chip, ci) => (
                                                        <React.Fragment key={ci}>
                                                            {ci > 0 && <span className={styles.metaDot}>•</span>}
                                                            <span>{chip}</span>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            )}

                                            <button className={styles.previewBtn} onClick={openPreview}>
                                                Preview
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {previewVisible && (
                        <TemplatePreview
                            visible={previewVisible}
                            onClose={() => setPreviewVisible(false)}
                            template={previewTemplate}
                            onVariableChange={handleVariableChange}
                            onTemplateSelect={getCampaignDetails}
                            data={data}
                            setData={setData}
                        />
                    )}

                    {mobileSheetOpen && (
                        <div className={styles.mobileSheetBackdrop} onClick={() => setMobileSheetOpen(false)}>
                            <section className={styles.mobileSheet} onClick={(e) => e.stopPropagation()}>
                                <div className={styles.mobileSheetHeader}>
                                    <h3 className={styles.mobileSheetTitle}>Filters &amp; Sort</h3>
                                    <button
                                        type="button"
                                        className={styles.mobileSheetClose}
                                        onClick={() => setMobileSheetOpen(false)}
                                        aria-label="Close"
                                    >
                                        <AiOutlineClose size={16} />
                                    </button>
                                </div>

                                <div className={styles.mobileSheetSection}>
                                    <span className={styles.sortLabel}>Sort by</span>
                                    <div className={styles.mobileSortOptions}>
                                        {SORT_FIELDS.map((field) => (
                                            <button
                                                key={field.key}
                                                type="button"
                                                className={`${styles.sortBtn} ${sortConfig.key === field.key ? styles.sortBtnActive : ''}`}
                                                onClick={() => handleSort(field.key)}
                                            >
                                                {field.label}
                                                {sortConfig.key === field.key ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className={styles.mobileSheetSection}>
                                    <span className={styles.sortLabel}>Filter</span>
                                    <div className={styles.mobileFilterOptions}>
                                        <select
                                            className={styles.filterSelect}
                                            value={filters.category}
                                            onChange={(e) => handleFilterChange('category', e.target.value)}
                                        >
                                            <option value="">All categories</option>
                                            {filterOptions.categories.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        <select
                                            className={styles.filterSelect}
                                            value={filters.language}
                                            onChange={(e) => handleFilterChange('language', e.target.value)}
                                        >
                                            <option value="">All languages</option>
                                            {filterOptions.languages.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className={styles.resetBtn}
                                            onClick={resetFilters}
                                            disabled={!hasActiveFilters && sortConfig.key === null}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>

                                <button type="button" className={styles.mobileSheetDone} onClick={() => setMobileSheetOpen(false)}>
                                    Done
                                </button>
                            </section>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default MessageTemplates;
